import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { CloseCircle } from 'asset';
import { Inputs } from 'components/form-control';
import yup from 'config/yup.custom';
import { NotifyService } from 'helpers';
import { JOB_TRANSACTION_TYPE, UPDATE_TRANSACTION_AC_BODY } from 'models';
import { memo, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { updateTransactionACApi } from 'services';

interface IFormInputs {
  amount: number | string;
  description?: string;
}

const defaultValues: Partial<IFormInputs> = {
  amount: '',
  description: '',
};

interface Props {
  open: boolean;
  subject?: JOB_TRANSACTION_TYPE;
  onSuccess: () => void;
  handleClosePopup: () => void;
}

const PopupEditACJobTransaction = memo(
  ({ open, subject, onSuccess, handleClosePopup }: Props) => {
    const schema = useMemo(() => {
      return yup.object({
        amount: yup
          .number()
          .typeError('Amount is required')
          .min(0, 'Amount must be greater than or equal to 0')
          .required('Amount is required'),
        description: yup.string().notRequired(),
      });
    }, []);

    const form = useForm<IFormInputs>({
      resolver: yupResolver(schema),
      mode: 'onChange',
      defaultValues: defaultValues,
    });

    const { reset, handleSubmit } = form;

    useEffect(() => {
      if (subject) {
        reset({
          amount: subject.amount,
          description: subject.description || '',
        });
      }
    }, [subject, reset]);

    const _handleClosePopup = () => {
      reset({ ...defaultValues });
      handleClosePopup();
    };

    const onSubmit = async (data: IFormInputs) => {
      if (subject) {
        const body: UPDATE_TRANSACTION_AC_BODY = {
          amount: data.amount as string,
          description: data.description || '',
        };
        await updateTransactionACApi(subject.id, body)
          .then((r) => {
            _handleClosePopup();
            onSuccess();
            NotifyService.success(r);
          })
          .catch((err) => NotifyService.error(err));
      }
    };

    return (
      <Dialog
        open={open}
        sx={{
          '& .MuiPaper-root': {
            minWidth: 523,
            borderRadius: 0,
          },
        }}
      >
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 25px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '100%',
            }}
          >
            Edit Transaction
          </Typography>
          <IconButton onClick={_handleClosePopup} sx={{ p: 0 }}>
            <CloseCircle />
          </IconButton>
        </Grid>
        <Divider />
        <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
          <Stack
            p="20px 24px 50px 26px"
            height="100%"
            justifyContent="space-between"
          >
            <Stack spacing="16px">
              <Stack>
                <Grid>
                  <Inputs
                    type="number"
                    title="Amount"
                    form={form}
                    name="amount"
                    placeholder="Enter amount"
                  />
                </Grid>
              </Stack>
              <Grid>
                <Inputs
                  title="Description"
                  form={form}
                  name="description"
                  multiline
                  rows={3}
                  placeholder="Description (optional)"
                />
              </Grid>
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              spacing="8px"
              pt="42px"
            >
              <Button
                variant="outlined"
                color="secondary"
                sx={{
                  width: '233.5px',
                  height: '30px',
                }}
                onClick={_handleClosePopup}
              >
                Cancel
              </Button>
              <Button
                color="secondary"
                variant="contained"
                sx={{
                  width: '233.5px',
                  height: '30px',
                }}
                type="submit"
              >
                {subject ? 'Update' : 'Create'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Dialog>
    );
  }
);

export default PopupEditACJobTransaction;
